import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "antd";
import InputField from "../../../../reusable-components/TNInputField";
import JobDatabaseService from "../../../../services/jobDatabase.service";
import { filterFieldConsts, getFilterFieldList } from "./constants";
import { getFilteredOptions } from "../../../../utils/getDropdownOptions";
import { useFormContext } from "react-hook-form";
import { hasAnyEmptyField } from "../../../../utils/emptyStringCheck";
import { PriorityValue } from "../../../../Types/jobDatabaseTypes";

interface FilterFormProps {
  visible: boolean;
  priorityMap: Record<string, PriorityValue>;
  setPriorityMap: React.Dispatch<
    React.SetStateAction<Record<string, PriorityValue>>
  >;
  isAffectedView?: boolean;
}

const FilterForm = ({
  visible,
  priorityMap,
  setPriorityMap,
  isAffectedView = false,
}: FilterFormProps) => {
  const isMountRef = useRef<boolean>(true);
  const { watch, resetField, setValue, getValues } = useFormContext();
  const [jobNoSearch, setJobNoSearch] = useState<undefined | string>("");
  const [jobNameSearch, setJobNameSearch] = useState<undefined | string>("");
  const [customerSearch, setCustomerSearch] = useState<undefined | string>("");
  const [jobTypeSearch, setJobTypeSearch] = useState<undefined | string>("");
  const [equipmentSearch, setEquipmentSearch] = useState<undefined | string>(
    "",
  );
  const [materialCategorySearch, setMaterialCategorySearch] = useState<
    undefined | string
  >("");
  const [materialTypeSearch, setMaterialTypeSearch] = useState<
    undefined | string
  >("");
  const [salesPersonSearch, setSalesPersonSearch] = useState<
    undefined | string
  >("");

  const {
    FetchJobNumberFilterMeta,
    FetchJobNameFilterMeta,
    FetchCustomerFilterMeta,
    FetchJobTypeFilterMeta,
    FetchEquipmentFilterMeta,
    FetchMaterialCatergoryFilterMeta,
    FetchMaterialTypeFilterMeta,
    FetchSalesPersonFilterMeta,
  } = JobDatabaseService();

  const { data: jobNos, mutateAsync: getJobNos } = FetchJobNumberFilterMeta();
  const { data: jobNames, mutateAsync: getJobNames } = FetchJobNameFilterMeta();
  const { data: customers, mutateAsync: getCustomers } =
    FetchCustomerFilterMeta();
  const { data: jobTypes, mutateAsync: getJobTypes } = FetchJobTypeFilterMeta();
  const { data: equipment, mutateAsync: getEquipment } =
    FetchEquipmentFilterMeta();
  const { data: materialCategories, mutateAsync: getMaterialCategories } =
    FetchMaterialCatergoryFilterMeta();
  const { data: materialTypes, mutateAsync: getMaterialTypes } =
    FetchMaterialTypeFilterMeta();
  const { data: salesPersonList, mutateAsync: getSalesPerson } =
    FetchSalesPersonFilterMeta();

  const jobNoOptions = useMemo(
    () => jobNos?.map((item) => ({ value: item, label: item })),
    [jobNos],
  );
  const jobNameOptions = useMemo(
    () => jobNames?.map((item) => ({ value: item?.id, label: item?.jobName })),
    [jobNames],
  );
  const customerOptions = useMemo(
    () =>
      customers?.map((item) => ({
        value: item?.companyId,
        label: item?.companyName,
      })),
    [customers],
  );
  const jobTypeOptions = useMemo(
    () => jobTypes?.map((item) => ({ value: item, label: item })),
    [jobTypes],
  );
  const equipmentOptions = useMemo(
    () => equipment?.map((item) => ({ value: item, label: item })),
    [equipment],
  );
  const materialCategoryOptions = useMemo(
    () => materialCategories?.map((item) => ({ value: item, label: item })),
    [materialCategories],
  );
  const materialTypeOptions = useMemo(
    () => materialTypes?.map((item) => ({ value: item, label: item })),
    [materialTypes],
  );
  const salesPersonOptions = useMemo(
    () =>
      salesPersonList?.map((item) => ({
        value: item?.id,
        label: item?.salesPerson,
      })),
    [salesPersonList],
  );

  const triggerRefetches = (changedField: string, clearedPriority?: number) => {
    const currentValues = getValues();

    if (clearedPriority !== undefined) {
      Object.entries(priorityMap)?.forEach(([field, priority]) => {
        if (field !== changedField) {
          const shouldRefetch =
            (priority !== "unselected" && priority > clearedPriority) ||
            priority === "unselected";

          if (shouldRefetch) {
            refetchField(field, currentValues);
          }
        } else {
          refetchField(field, currentValues);
        }
      });
    } else {
      const changePriority = priorityMap[changedField];
      Object.entries(priorityMap)?.forEach(([field, priority]) => {
        if (field !== changedField) {
          const shouldRefetch =
            priority === "unselected" ||
            (priority !== "unselected" && priority > changePriority);

          if (shouldRefetch) {
            refetchField(field, currentValues);
          }
        }
      });
    }
  };

  const refetchField = (field: string, currentFormValues?: any) => {
    const valuesToUse = currentFormValues || getValues();

    const payload = { ...valuesToUse, isAffectedView };
    delete payload[field];

    switch (field) {
      case filterFieldConsts.JOB_NUMBER:
        getJobNos(payload);
        break;
      case filterFieldConsts.JOB_NAME:
        getJobNames(payload);
        break;
      case filterFieldConsts.CUSTOMER_NAME:
        getCustomers(payload);
        break;
      case filterFieldConsts.JOB_TYPE:
        getJobTypes(payload);
        break;
      case filterFieldConsts.EQUIPMENT:
        getEquipment(payload);
        break;
      case filterFieldConsts.MATERIAL_CATEGORY:
        getMaterialCategories(payload);
        break;
      case filterFieldConsts.MATERIAL_TYPE:
        getMaterialTypes(payload);
        break;
      case filterFieldConsts.SALES_PERSON:
        getSalesPerson(payload);
        break;
    }
  };

  const getOptionsList = (fieldName: string) => {
    switch (fieldName) {
      case filterFieldConsts.JOB_NUMBER:
        return getFilteredOptions?.(jobNoOptions ?? [], jobNoSearch) ?? [];
      case filterFieldConsts.JOB_NAME:
        return getFilteredOptions?.(jobNameOptions ?? [], jobNameSearch) ?? [];
      case filterFieldConsts.CUSTOMER_NAME:
        return (
          getFilteredOptions?.(customerOptions ?? [], customerSearch) ?? []
        );
      case filterFieldConsts.JOB_TYPE:
        return getFilteredOptions?.(jobTypeOptions ?? [], jobTypeSearch) ?? [];
      case filterFieldConsts.EQUIPMENT:
        return (
          getFilteredOptions?.(equipmentOptions ?? [], equipmentSearch) ?? []
        );
      case filterFieldConsts.MATERIAL_CATEGORY:
        return (
          getFilteredOptions?.(
            materialCategoryOptions ?? [],
            materialCategorySearch,
          ) ?? []
        );
      case filterFieldConsts.MATERIAL_TYPE:
        return (
          getFilteredOptions?.(materialTypeOptions ?? [], materialTypeSearch) ??
          []
        );
      case filterFieldConsts.SALES_PERSON:
        return (
          getFilteredOptions?.(salesPersonOptions ?? [], salesPersonSearch) ??
          []
        );
      default:
        return [];
    }
  };

  const handleChange = (fieldName: string) => (value: any) => {
    const prevValue = getValues?.(fieldName);

    setValue(fieldName, value);

    const newHasValue = Array.isArray(value) ? value?.length > 0 : !!value;
    const prevHasValue = Array.isArray(prevValue)
      ? prevValue?.length > 0
      : !!prevValue;

    setPriorityMap((prevPriority) => {
      const updated = { ...prevPriority };

      if (!prevHasValue && newHasValue) {
        const selectedFields = Object.values(prevPriority)?.filter(
          (p): p is number => p !== "unselected",
        );
        const maxPriority =
          selectedFields?.length > 0 ? Math.max(...selectedFields) : 0;
        updated[fieldName] = maxPriority + 1;
      } else if (prevHasValue && !newHasValue) {
        const currentPriority = prevPriority[fieldName];
        updated[fieldName] = "unselected";

        if (currentPriority !== "unselected" && currentPriority > 0) {
          Object.entries?.(updated)?.forEach(([f, p]) => {
            if (p !== "unselected" && p > currentPriority) {
              updated[f] = p - 1;
            }
          });
        }
      }
      return updated;
    });

    setTimeout(() => {
      triggerRefetches(fieldName);
    }, 0);
  };

  const handleClear = (fieldName: string) => () => {
    const prevValue = getValues(fieldName);
    const prevHasValue = Array.isArray(prevValue)
      ? prevValue?.length > 0
      : !!prevValue;

    setValue(fieldName, []);

    if (prevHasValue) {
      const currentPriority = priorityMap[fieldName];

      setPriorityMap((prevPriority) => {
        const updated = { ...prevPriority };
        updated[fieldName] = "unselected";

        if (currentPriority !== "unselected" && currentPriority > 0) {
          Object.entries?.(updated).forEach?.(([f, p]) => {
            if (p !== "unselected" && p > currentPriority) {
              updated[f] = p - 1;
            }
          });
        }
        return updated;
      });

      if (currentPriority !== "unselected") {
        triggerRefetches(fieldName, currentPriority);
      } else {
        triggerRefetches(fieldName);
      }
    } else {
      setTimeout(() => {
        triggerRefetches(fieldName);
      }, 0);
    }
  };

  useEffect(() => {
    const initialPayload = { isAffectedView };
    getJobNos(initialPayload);
    getJobNames(initialPayload);
    getCustomers(initialPayload);
    getJobTypes(initialPayload);
    getEquipment(initialPayload);
    getMaterialCategories(initialPayload);
    getMaterialTypes(initialPayload);
    getSalesPerson(initialPayload);
  }, [isAffectedView]);

  const getPriorityDisplay = (fieldName: string) => {
    const priority = priorityMap[fieldName];
    return priority === "unselected" ? "Unselected" : `Priority ${priority}`;
  };

  return (
    <Row>
      {getFilterFieldList({
        jobNoSearch,
        jobNameSearch,
        customerSearch,
        jobTypeSearch,
        equipmentSearch,
        materialCategorySearch,
        materialTypeSearch,
        salesPersonSearch,
        searchJobNumbers: setJobNoSearch,
        searchJobNames: setJobNameSearch,
        searchCustomers: setCustomerSearch,
        searchTypes: setJobTypeSearch,
        searchEquipment: setEquipmentSearch,
        searchMaterialCategories: setMaterialCategorySearch,
        searchMaterialTypes: setMaterialTypeSearch,
        salesPersonTypes: setSalesPersonSearch,
        isAffectedView,
      })?.map((item) => {
        return (
          <Col span={12} key={item.name}>
            <InputField
              type={item.type}
              name={item.name}
              label={item.label}
              placeholder={item.placeholder}
              bordered={item.bordered}
              options={getOptionsList(item.name)}
              setSearch={item.setSearch}
              values={watch(item.name)}
              onChange={handleChange(item.name)}
              resetCheckbox={handleClear(item.name)}
              searchValue={item.searchValue}
            />
          </Col>
        );
      })}
    </Row>
  );
};

export default FilterForm;
